<!-- InputTextarea: A textarea field input -->
<template>
    <div class="flex flex-col min-h-0">        
        <div v-if="textControls" class="mb-2 space-y-2 flex-shrink-0">
            <!-- Primary Formatting Row -->
            <div class="flex gap-0 flex-wrap items-center p-0 bg-gray-50 rounded border">
                <Button @click="format('bold')" title="Bold" icon="bold" class="w-8 " :rounded="false" />
                <Button @click="format('italic')" title="Italic" icon="italic" class=" " :rounded="false" />
                <Button @click="format('underline')" title="Underline" icon="underline" class=" " :rounded="false" />
                <div class="w-px h-6 bg-gray-300 mx-0.5"></div>
                <Button @click="format('header')" title="Header Text" text="sm" label="H" class="w-8  text-xs font-bold" :rounded="false" />
                <Button icon="chevron-up" @click="format('large')" title="Large Text" class=" " :rounded="false" />
                <Button icon="chevron-down" @click="format('small')" title="Small Text" class=" " :rounded="false" />
                <div class="w-px h-6 bg-gray-300 mx-0.5"></div>                
                <Button icon="bars-3-bottom-left" @click="applyAlignFormat('left')" title="Align Left" class=" " :rounded="false" />
                <Button icon="bars-3" @click="applyAlignFormat('center')" title="Align Center" class=" " :rounded="false" />
                <Button icon="bars-3-bottom-right" @click="applyAlignFormat('right')" title="Align Right" class=" " :rounded="false" />
                <div class="w-px h-6 bg-gray-300 mx-0.5"></div>                
                <Button icon="link" @click="showFormatPanel('link')" text="sm" title="Link" label="Link" class="text-xs" :rounded="false" />
                <Button icon="photo" @click="showFormatPanel('image')" text="sm" title="Image" label="Image" class="text-xs" :rounded="false" />
                <Button @click="showFormatPanel('list')" title="Lists" icon="list-bullet" text="sm" label="List" class="text-xs" :rounded="false" />                
                <Button @click="showFormatPanel('colors')" title="Colors & Effects" icon="swatch" text="sm" label="Colors" class="text-xs" :rounded="false" />
            </div>
        </div>

        <!-- Formatting Panel -->
        <div v-if="showPanel" class="mb-2 p-3 border rounded-sm bg-gray-50 border-gray-300 flex-shrink-0">
            <!-- Link Panel -->
            <div v-if="currentPanel === 'link'" class="space-y-2">
                <h4 class="font-semibold text-sm">Insert Link</h4>
                <div class="flex flex-col gap-2">
                    <input 
                        v-model="linkUrl" 
                        type="text" 
                        placeholder="Enter URL (including https://)"
                        class="border rounded px-2 py-1 text-sm"
                        @keydown.enter="applyLinkFormat"
                    />
                    <input 
                        v-model="linkText" 
                        type="text" 
                        placeholder="Link text (optional - uses URL if empty)"
                        class="border rounded px-2 py-1 text-sm"
                        @keydown.enter="applyLinkFormat"
                    />
                    <div class="flex gap-2">
                        <Button @click="applyLinkFormat" label="Insert Link" class="text-xs" />
                        <Button @click="cancelFormat" label="Cancel" class="text-xs" />
                    </div>
                </div>
            </div>

            <!-- Image Panel -->
            <div v-if="currentPanel === 'image'" class="space-y-2">
                <h4 class="font-semibold text-sm">Insert Image</h4>
                <div class="flex flex-col gap-2">
                    <input 
                        v-model="imageUrl" 
                        type="text" 
                        placeholder="Enter image URL"
                        class="border rounded px-2 py-1 text-sm"
                        @keydown.enter="applyImageFormat"
                    />
                    <select v-model="imageAlign" class="border rounded px-2 py-1 text-sm">
                        <option value="center">Center</option>
                        <option value="left">Left</option>
                        <option value="right">Right</option>
                        <option value="">No alignment</option>
                    </select>
                    <div class="flex gap-2">
                        <Button @click="applyImageFormat" label="Insert Image" class="text-xs" />
                        <Button @click="cancelFormat" label="Cancel" class="text-xs" />
                    </div>
                </div>
            </div>

            <!-- Colors Panel -->
            <div v-if="currentPanel === 'colors'" class="space-y-3">
                <h4 class="font-semibold text-sm">Colors & Effects</h4>
                
                <!-- Color Type Selection -->
                <div class="flex gap-2 border-b pb-2 w-full justify-between">
                    <Button @click="colorType = 'text'" :padding="1" text="sm"
                        :class="`px-3 py-1 text-xs rounded w-1/4 ${colorType === 'text' ? 'opacity-100' : 'opacity-50'}`"
                     label="Text Color" />
                     
                    <Button label="Background"
                        @click="colorType = 'background'" :padding="1" text="sm"
                        :class="`px-3 py-1 text-xs rounded w-1/4  ${colorType === 'background' ? 'opacity-100' : 'opacity-50'}`"
                     />
                    <Button label="Highlight"
                        @click="colorType = 'highlight'" :padding="1" text="sm"
                        :class="`px-3 py-1 text-xs rounded w-1/4 ${colorType === 'highlight' ? 'opacity-100' : 'opacity-50'}`"
                     />
                    <Button label="Alert Box"
                        @click="colorType = 'alert'" :padding="1" text="sm"
                        :class="`px-3 py-1 text-xs rounded w-1/4 ${colorType === 'alert' ? 'opacity-100' : 'opacity-50'}`"
                     />
                </div>

                <!-- Color Selection for Text and Background -->
                <div v-if="colorType === 'text' || colorType === 'background'" class="space-y-2">
                    <div class="flex gap-2 flex-wrap">
                        <Button 
                            v-for="color in (colorType === 'text' ? commonColors : commonBgColors)" 
                            :key="color.value"
                            @click="selectedColor = color.value"
                            :class="`w-6 h-6 rounded border-2 ${selectedColor === color.value ? 'border-gray-600' : 'border-gray-300'}`"
                            :style="`background-color: ${color.value}`"
                            :title="color.name" />
                    </div>
                    <input 
                        v-model="selectedColor" 
                        type="text" 
                        :placeholder="colorType === 'text' ? 'Enter custom color (#ff0000 or red)' : 'Enter custom color (#ffff00 or yellow)'"
                        class="border rounded px-2 py-1 text-sm w-full"
                        @keydown.enter="applyColorEffect"
                    />
                </div>

                <!-- Alert and Highlight Info -->
                <div v-else-if="colorType === 'highlight'" class="text-sm text-gray-600">
                    Creates highlighted text with a yellow background
                </div>
                <div v-else-if="colorType === 'alert'" class="text-sm text-gray-600">
                    Creates an alert box with border and background styling
                </div>

                <!-- Action Buttons -->
                <div class="flex gap-2 pt-2 border-t">
                    <Button @click="applyColorEffect" :label="`Apply ${colorType.charAt(0).toUpperCase() + colorType.slice(1)}`" class="text-xs flex-1" />
                    <Button @click="cancelFormat" label="Cancel" class="text-xs" />
                </div>
            </div>

            <!-- Size Panel -->
            <div v-if="currentPanel === 'size'" class="space-y-2">
                <h4 class="font-semibold text-sm">Font Size</h4>
                <div class="flex flex-col gap-2">
                    <select v-model="selectedSize" class="border rounded px-2 py-1 text-sm">
                        <option value="8px">Tiny (8px)</option>
                        <option value="10px">Small (10px)</option>
                        <option value="12px">Normal (12px)</option>
                        <option value="14px">Medium (14px)</option>
                        <option value="16px">Large (16px)</option>
                        <option value="20px">Extra Large (20px)</option>
                        <option value="24px">Huge (24px)</option>
                        <option value="32px">Giant (32px)</option>
                    </select>
                    <input 
                        v-model="selectedSize" 
                        type="text" 
                        placeholder="Or enter custom size (16px, 1.2em, etc.)"
                        class="border rounded px-2 py-1 text-sm"
                        @keydown.enter="applySizeFormat"
                    />
                    <div class="flex gap-2">
                        <Button @click="applySizeFormat" label="Apply Size" class="text-xs" />
                        <Button @click="cancelFormat" label="Cancel" class="text-xs" />
                    </div>
                </div>
            </div>

            <!-- List Panel -->
            <div v-if="currentPanel === 'list'" class="space-y-2">
                <h4 class="font-semibold text-sm">Create List</h4>
                <div class="flex flex-col gap-2">
                    <textarea 
                        v-model="listItems" 
                        placeholder="Enter list items, one per line"
                        rows="4"
                        class="border rounded px-2 py-1 text-sm"
                    ></textarea>
                    <div class="flex gap-2">
                        <Button 
                            @click="applyListFormat('bullet')" 
                            label="Bullet List" 
                            class="text-xs flex-1"
                        />
                        <Button 
                            @click="applyListFormat('numbered')" 
                            label="Numbered List" 
                            class="text-xs flex-1"
                        />
                    </div>
                    <div class="flex gap-2">
                        <Button @click="cancelFormat" label="Cancel" class="text-xs" />
                    </div>
                </div>
            </div>
        </div>

        <textarea :id="id"   
            ref="refInput"
            :tabindex="index"   
            v-model="input"             
            :class="`border w-full text-left p-1 rounded-sm mn3-input self-stretch`"
            :style="`min-height: ${textControls ? '200px' : '100px'}; resize: vertical;`">
        </textarea>
    </div>
</template>
<script>

import { ref, computed } from "vue";

import Button from '@/components/Button.vue'
import Icon from '@/components/Icon.vue'
export default{
    components: {
        Button,
        Icon
    },
    props:{
        /* Properties without defaults */
        id: String,                     //id: ID assigned to the input
        index: Number,                  //index: TabIndex assigned to the input
        textControls: Boolean,
        
        /* Properties with defaults */
        formValue: {                    //formValue: Used to store field input    
            type: Object,
            default: ref("")
        },
    },
    computed: {   
    },
    methods:{     
        format(type) {
            let textarea = this.$refs.refInput;
            if (!textarea) return;
            let [start, end] = [textarea.selectionStart, textarea.selectionEnd];
            let value = this.formValue.value;
            let before = value.substring(0, start);
            let selected = value.substring(start, end);
            let after = value.substring(end);

            let tagOpen = '', tagClose = '';
            if (type === 'bold') {
                tagOpen = '[b]'; tagClose = '[/b]';
            } else if (type === 'italic') {
                tagOpen = '[i]'; tagClose = '[/i]';
            } else if (type === 'header') {
                tagOpen = '[header]'; tagClose = '[/header]';
            } else {
                tagOpen = '['+type+']'; 
                tagClose = '[/'+type+']';
            }

            // Check if the selected text already starts and ends with these tags
            if (selected.startsWith(tagOpen) && selected.endsWith(tagClose)) {
                // Remove the tags from the selected text
                let innerText = selected.substring(tagOpen.length, selected.length - tagClose.length);
                this.formValue.value = before + innerText + after;
                
                // Update cursor position to select the inner text
                this.$nextTick(() => {
                    textarea.focus();
                    textarea.setSelectionRange(start, start + innerText.length);
                });
            } else {
                // Add the tags around the selected text
                this.formValue.value = before + tagOpen + selected + tagClose + after;
                
                // Update cursor position to select the formatted text
                this.$nextTick(() => {
                    textarea.focus();
                    textarea.setSelectionRange(start, start + tagOpen.length + selected.length + tagClose.length);
                });
            }
        },
        showFormatPanel(type) {
            // Toggle behavior - if clicking the same panel button, close it
            if (this.currentPanel === type && this.showPanel) {
                this.cancelFormat();
                return;
            }
            
            this.currentPanel = type;
            this.showPanel = true;
            this.storeSelection();
            
            // Reset form values
            if (type === 'link') {
                this.linkUrl = '';
                this.linkText = this.getSelectedText();
            } else if (type === 'image') {
                this.imageUrl = '';
                this.imageAlign = 'center';
            } else if (type === 'colors') {
                this.colorType = 'text';
                this.selectedColor = '#000000';
            } else if (type === 'size') {
                this.selectedSize = '16px';
            } else if (type === 'list') {
                // Pre-populate with selected text if available
                let selected = this.getSelectedText();
                if (selected) {
                    this.listItems = selected;
                } else {
                    this.listItems = '';
                }
            }
        },
        cancelFormat() {
            this.showPanel = false;
            this.currentPanel = '';
        },
        storeSelection() {
            let textarea = this.$refs.refInput;
            if (!textarea) return;
            this.selectionStart = textarea.selectionStart;
            this.selectionEnd = textarea.selectionEnd;
        },
        getSelectedText() {
            let textarea = this.$refs.refInput;
            if (!textarea) return '';
            return this.formValue.value.substring(textarea.selectionStart, textarea.selectionEnd);
        },
        applyLinkFormat() {
            if (!this.linkUrl) return;
            
            let displayText = this.linkText || this.getSelectedText() || this.linkUrl;
            let tagOpen = `[link=${this.linkUrl}]`;
            let tagClose = '[/link]';
            
            this.insertFormattedText(tagOpen, displayText, tagClose);
            this.cancelFormat();
        },
        applyImageFormat() {
            if (!this.imageUrl) return;
            
            let imgTag = `[img]${this.imageUrl}[/img]`;
            
            // Apply alignment if specified
            if (this.imageAlign && this.imageAlign !== '') {
                if (this.imageAlign === 'center') {
                    imgTag = `[center]${imgTag}[/center]`;
                } else if (this.imageAlign === 'left') {
                    imgTag = `[left]${imgTag}[/left]`;
                } else if (this.imageAlign === 'right') {
                    imgTag = `[right]${imgTag}[/right]`;
                }
            }
            
            this.insertFormattedText('', imgTag, '');
            this.cancelFormat();
        },
        applyColorEffect() {
            let selected = this.getSelectedText();
            
            // For highlight and alert, we can work without selection
            if (!selected && this.colorType !== 'alert' && this.colorType !== 'highlight') {
                // For text and background colors, insert tags at cursor position if no selection
                if (this.colorType === 'text' && this.selectedColor) {
                    this.insertFormattedText(`[color=${this.selectedColor}]`, '', '[/color]');
                    this.repositionCursorInTags(`[color=${this.selectedColor}]`, '[/color]');
                } else if (this.colorType === 'background' && this.selectedColor) {
                    this.insertFormattedText(`[bgcolor=${this.selectedColor}]`, '', '[/bgcolor]');
                    this.repositionCursorInTags(`[bgcolor=${this.selectedColor}]`, '[/bgcolor]');
                }
                this.cancelFormat();
                return;
            }
            
            let tagOpen = '', tagClose = '';
            if (this.colorType === 'text') {
                if (!this.selectedColor) return;
                tagOpen = `[color=${this.selectedColor}]`;
                tagClose = '[/color]';
            } else if (this.colorType === 'background') {
                if (!this.selectedColor) return;
                tagOpen = `[bgcolor=${this.selectedColor}]`;
                tagClose = '[/bgcolor]';
            } else if (this.colorType === 'highlight') {
                tagOpen = '[highlight]';
                tagClose = '[/highlight]';
            } else if (this.colorType === 'alert') {
                tagOpen = '[alert]';
                tagClose = '[/alert]';
            }
            
            this.insertFormattedText(tagOpen, selected, tagClose);
            
            // Re-highlight the formatted text
            this.rehighlightFormattedText(tagOpen, selected, tagClose);
            
            this.cancelFormat();
        },
        applySizeFormat() {
            if (!this.selectedSize) return;
            
            let selected = this.getSelectedText();
            if (!selected) {
                alert('Please select some text first');
                return;
            }
            
            this.insertFormattedText(`[size=${this.selectedSize}]`, selected, '[/size]');
            this.cancelFormat();
        },
        applyAlignFormat(alignment) {
            let textarea = this.$refs.refInput;
            if (!textarea) return;
            
            let [start, end] = [textarea.selectionStart, textarea.selectionEnd];
            let selected = this.formValue.value.substring(start, end);
            
            if (!selected) {
                alert('Please select some text first');
                return;
            }
            
            let tagOpen = '', tagClose = '';
            if (alignment === 'center') {
                tagOpen = '[center]';
                tagClose = '[/center]';
            } else if (alignment === 'left') {
                tagOpen = '[left]';
                tagClose = '[/left]';
            } else if (alignment === 'right') {
                tagOpen = '[right]';
                tagClose = '[/right]';
            }
            
            let value = this.formValue.value;
            let before = value.substring(0, start);
            let after = value.substring(end);
            
            this.formValue.value = before + tagOpen + selected + tagClose + after;
            
            // Update cursor position to select the formatted text
            this.$nextTick(() => {
                textarea.focus();
                textarea.setSelectionRange(start, start + tagOpen.length + selected.length + tagClose.length);
            });
        },
        applyListFormat(listType) {
            if (!this.listItems.trim()) {
                alert('Please enter some list items');
                return;
            }
            
            let items = this.listItems.trim().split('\n').filter(item => item.trim() !== '');
            let listContent = '';
            
            if (listType === 'bullet') {
                items.forEach(item => {
                    listContent += `• ${item.trim()}\n`;
                });
            } else if (listType === 'numbered') {
                items.forEach((item, index) => {
                    listContent += `${index + 1}. ${item.trim()}\n`;
                });
            }
            
            // Remove last newline
            listContent = listContent.replace(/\n$/, '');
            
            this.insertFormattedText('', listContent, '');
            this.cancelFormat();
        },
        insertFormattedText(tagOpen, content, tagClose) {
            let value = this.formValue.value;
            let before = value.substring(0, this.selectionStart);
            let after = value.substring(this.selectionEnd);
            
            this.formValue.value = before + tagOpen + content + tagClose + after;
        },
        repositionCursorInTags(tagOpen, tagClose) {
            // Position cursor between the tags for typing
            let newPosition = this.selectionStart + tagOpen.length;
            this.$nextTick(() => {
                let textarea = this.$refs.refInput;
                if (textarea) {
                    textarea.focus();
                    textarea.setSelectionRange(newPosition, newPosition);
                }
            });
        },
        rehighlightFormattedText(tagOpen, content, tagClose) {
            // Re-select the entire formatted text including tags
            let startPos = this.selectionStart;
            let endPos = startPos + tagOpen.length + content.length + tagClose.length;
            this.$nextTick(() => {
                let textarea = this.$refs.refInput;
                if (textarea) {
                    textarea.focus();
                    textarea.setSelectionRange(startPos, endPos);
                }
            });
        },
        focus(){
            this.refInput.focus();
        },   
    },
    setup(props, { emit }){
        
        const refInput = ref(null);
        /* --Variables-- */        
        const val = ref(props.formValue);   //val: Used in computed input property

        // Panel state
        const showPanel = ref(false);
        const currentPanel = ref('');
        const selectionStart = ref(0);
        const selectionEnd = ref(0);
        const showAdvanced = ref(false);

        // Link formatting
        const linkUrl = ref('');
        const linkText = ref('');

        // Image formatting
        const imageUrl = ref('');
        const imageAlign = ref('center');

        // Color formatting
        const selectedColor = ref('#000000');
        const colorType = ref('text');
        const commonColors = ref([
            { name: 'Black', value: '#000000' },
            { name: 'Red', value: '#ff0000' },
            { name: 'Green', value: '#008000' },
            { name: 'Blue', value: '#0000ff' },
            { name: 'Orange', value: '#ffa500' },
            { name: 'Purple', value: '#800080' },
            { name: 'Brown', value: '#a52a2a' },
            { name: 'Gray', value: '#808080' }
        ]);

        const commonBgColors = ref([
            { name: 'Yellow', value: '#ffff00' },
            { name: 'Light Blue', value: '#add8e6' },
            { name: 'Light Green', value: '#90ee90' },
            { name: 'Light Pink', value: '#ffb6c1' },
            { name: 'Light Orange', value: '#ffd4a3' },
            { name: 'Light Purple', value: '#dda0dd' },
            { name: 'Light Gray', value: '#d3d3d3' },
            { name: 'White', value: '#ffffff' }
        ]);

        // Size formatting
        const selectedSize = ref('16px');

        // Alignment formatting
        const selectedAlign = ref('');

        // List formatting
        const listItems = ref('');

        /* --Computed-- */        
        const input                         //input: holds input value, emits updated event on change
            = computed({
            get: () => val.value,
            set: (v) => (val.value = v,emit('updated', formValue => Object.keys(formValue)[0], v))
        })

        /* --Functions-- */        
        
        return {
            input,
            refInput,
            showPanel,
            currentPanel,
            selectionStart,
            selectionEnd,
            showAdvanced,
            linkUrl,
            linkText,
            imageUrl,
            imageAlign,
            selectedColor,
            colorType,
            commonColors,
            commonBgColors,
            selectedSize,
            selectedAlign,
            listItems
        }
    }
}

</script>